import React, { useState } from 'react';
import { 
  Card, 
  List, 
  Typography, 
  Tag, 
  Descriptions, 
  Empty, 
  Button, 
  Modal, 
  Row, 
  Col,
  Tooltip,
  Space,
  Divider,
  Timeline
} from 'antd';
import {
  ExperimentOutlined,
  InfoCircleOutlined,
  RiseOutlined,
  FallOutlined,
  EyeOutlined,
  DownloadOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import moment from 'moment';
import { getLabValueStatusWithFlag, getLabValueStyle, getLabStatusText } from '../utils/labRangeUtils';

const { Title, Text, Paragraph } = Typography;

const PatientPortalLabResults = ({ data, loading }) => {
  const [selectedResult, setSelectedResult] = useState(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [trendModalVisible, setTrendModalVisible] = useState(false);
  const [selectedComponent, setSelectedComponent] = useState(null);

  // Helper function to determine if a lab value is normal using enhanced range checking
  const getLabStatus = (component) => {
    const status = getLabValueStatusWithFlag(component.value, component.referenceRange, component.flag);

    // Map our detailed status to patient portal categories
    switch (status) {
      case 'critical-high':
      case 'critical-low':
        return 'critical';
      case 'high':
      case 'low':
        return 'abnormal';
      case 'normal':
        return 'normal';
      default:
        return 'normal';
    }
  };

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'normal': return '#000000'; // Changed to black
      case 'abnormal': return '#fa8c16';
      case 'critical': return '#ff4d4f';
      default: return '#8c8c8c';
    }
  };

  // Helper function to get patient-friendly explanations
  const getPatientFriendlyExplanation = (testName) => {
    const explanations = {
      'Complete Blood Count': 'Measures different types of blood cells to check for infections, anemia, and other blood disorders.',
      'Basic Metabolic Panel': 'Checks kidney function, blood sugar, and electrolyte balance.',
      'Comprehensive Metabolic Panel': 'Comprehensive check of kidney and liver function, blood sugar, and electrolytes.',
      'Lipid Panel': 'Measures cholesterol and triglycerides to assess heart disease risk.',
      'Thyroid Function': 'Checks how well your thyroid gland is working.',
      'Hemoglobin A1C': 'Shows average blood sugar levels over the past 2-3 months.',
      'Liver Function Tests': 'Checks how well your liver is working.',
      'Kidney Function Tests': 'Measures how well your kidneys are filtering waste from your blood.'
    };
    return explanations[testName] || 'Laboratory test to check various aspects of your health.';
  };

  // Helper function to format lab component for display
  const formatLabComponent = (component) => {
    const status = getLabStatus(component);
    const statusColor = getStatusColor(status);

    // Get enhanced styling for the value
    const detailedStatus = getLabValueStatusWithFlag(component.value, component.referenceRange, component.flag);
    const enhancedStyle = getLabValueStyle(detailedStatus);

    return (
      <div key={component.testName} className="lab-component">
        <div className="lab-component-name">
          <Text strong>{component.testName}</Text>
          {component.category && (
            <Tag size="small" style={{ marginLeft: 8 }}>{component.category}</Tag>
          )}
        </div>
        <div className="lab-component-value">
          <span className={`lab-value ${status}`} style={enhancedStyle}>
            {component.value} {component.units}
          </span>
          {(component.flag || detailedStatus !== 'normal') && (
            <Tooltip title={`Status: ${getLabStatusText(detailedStatus)}`}>
              <Tag color={status === 'critical' ? 'red' : status === 'abnormal' ? 'orange' : 'green'}>
                {component.flag || detailedStatus.toUpperCase()}
              </Tag>
            </Tooltip>
          )}
        </div>
        {component.referenceRange && (
          <div className="lab-reference-range">
            Normal range: {component.referenceRange}
          </div>
        )}
      </div>
    );
  };

  // Show detailed view of a lab result
  const showDetailModal = (result) => {
    setSelectedResult(result);
    setDetailModalVisible(true);
  };

  // Show trend analysis for a specific component
  const showTrendModal = (component, result) => {
    setSelectedComponent({ ...component, testDate: result.results?.resultDate });
    setTrendModalVisible(true);
  };

  // Group lab components by category with proper ordering
  const groupComponentsByCategory = (components) => {
    const grouped = {};
    components.forEach(component => {
      const category = component.category || 'Other';
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(component);
    });

    // Define specific test ordering within categories
    const getTestOrder = (testName, category) => {
      if (category === 'Complete Blood Count') {
        // CBC order: White Blood Cells → Red Blood Cells → Platelets
        // Using exact display names from parameterDisplayNames.js
        const cbcOrder = [
          // White Blood Cells section
          'White Blood Cell Count',
          'Neutrophils', 'Neutrophils',  // Note: both absolute and % use same display name
          'Lymphocytes', 'Lymphocytes',
          'Monocytes', 'Monocytes',
          'Eosinophils', 'Eosinophils',
          'Basophils', 'Basophils',
          // Red Blood Cells section
          'Red Blood Cell Count',
          'Hemoglobin',
          'Hematocrit',
          'Mean Corpuscular Volume',
          'Mean Corpuscular Hemoglobin',
          'Mean Corpuscular Hemoglobin Concentration',
          'Red Cell Distribution Width',
          'Red Cell Distribution Width-SD',
          // Platelets section
          'Platelet Count',
          'Mean Platelet Volume',
          'Platelet Distribution Width',
          'Plateletcrit',
          'Platelet Large Cell Ratio'
        ];
        const index = cbcOrder.indexOf(testName);
        return index >= 0 ? index : 999;
      }
      return 0; // Default order for other categories
    };

    // Sort tests within each category
    Object.keys(grouped).forEach(category => {
      grouped[category].sort((a, b) => {
        const orderA = getTestOrder(a.testName, category);
        const orderB = getTestOrder(b.testName, category);
        if (orderA !== orderB) {
          return orderA - orderB;
        }
        // If same order or not in predefined order, sort alphabetically
        return a.testName.localeCompare(b.testName);
      });
    });

    // Define category order for better clinical organization
    const categoryOrder = [
      'Complete Blood Count',
      'Lipid Panel',
      'Glucose & Diabetes',
      'Liver Function',
      'Kidney Function',
      'Electrolytes',
      'Thyroid Function',
      'Vitamins & Minerals',
      'Inflammation',
      'Coagulation',
      'Protein',
      'Cardiac Markers',
      'Urinalysis',
      'General',
      'Other'
    ];

    // Sort categories according to clinical importance
    const sortedGrouped = {};
    categoryOrder.forEach(category => {
      if (grouped[category]) {
        sortedGrouped[category] = grouped[category];
      }
    });

    // Add any remaining categories not in the predefined order
    Object.keys(grouped).forEach(category => {
      if (!sortedGrouped[category]) {
        sortedGrouped[category] = grouped[category];
      }
    });

    return sortedGrouped;
  };

  if (data.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <span>
            No lab results found
            <br />
            <Text type="secondary">Your lab results will appear here once available</Text>
          </span>
        }
      />
    );
  }

  return (
    <div>
      <List
        dataSource={data}
        renderItem={(result) => {
          const groupedComponents = groupComponentsByCategory(result.results?.labComponents || []);
          const abnormalCount = result.results?.labComponents?.filter(c => getLabStatus(c) !== 'normal').length || 0;
          
          return (
            <List.Item style={{ padding: 0, marginBottom: 16 }}>
              <Card className="lab-result-card" style={{ width: '100%' }}>
                <div className="lab-result-header">
                  <div>
                    <Title level={5} className="lab-result-title">
                      <ExperimentOutlined style={{ marginRight: 8, color: '#52c41a' }} />
                      {result.orderDetails?.testName || 'Lab Test'}
                    </Title>
                    <Text type="secondary" className="lab-result-date">
                      <CalendarOutlined style={{ marginRight: 4 }} />
                      {moment(result.results?.resultDate).format('MMMM DD, YYYY')}
                    </Text>
                    {abnormalCount > 0 && (
                      <Tag color="orange" style={{ marginLeft: 8 }}>
                        {abnormalCount} abnormal value{abnormalCount > 1 ? 's' : ''}
                      </Tag>
                    )}
                  </div>
                  <Space>
                    <Button 
                      icon={<EyeOutlined />} 
                      onClick={() => showDetailModal(result)}
                      size="small"
                    >
                      View Details
                    </Button>
                    <Button 
                      icon={<DownloadOutlined />} 
                      size="small"
                      type="text"
                    >
                      Export
                    </Button>
                  </Space>
                </div>

                {/* Patient-friendly explanation */}
                <div style={{ marginBottom: 16, padding: 12, background: '#f6f8fa', borderRadius: 6 }}>
                  <Text style={{ fontSize: 13, color: '#666' }}>
                    <InfoCircleOutlined style={{ marginRight: 4 }} />
                    {getPatientFriendlyExplanation(result.orderDetails?.testName)}
                  </Text>
                </div>

                {/* Lab components grouped by category */}
                {Object.entries(groupedComponents).map(([category, components]) => (
                  <div key={category} style={{ marginBottom: 20 }}>
                    <div style={{
                      borderLeft: '4px solid #1890ff',
                      paddingLeft: 12,
                      marginBottom: 12,
                      backgroundColor: '#f0f9ff',
                      padding: '8px 12px',
                      borderRadius: '4px'
                    }}>
                      <Title level={5} style={{
                        color: '#1890ff',
                        margin: 0,
                        fontSize: '14px',
                        fontWeight: 600
                      }}>
                        {category} ({components.length} tests)
                      </Title>
                    </div>
                    <div className="lab-components-grid">
                      {components.map(component => (
                        <div key={component.testName} style={{ position: 'relative' }}>
                          {formatLabComponent(component)}
                          <Button
                            size="small"
                            type="text"
                            icon={<RiseOutlined />}
                            style={{ position: 'absolute', top: 4, right: 4 }}
                            onClick={() => showTrendModal(component, result)}
                            title="View trend"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

                {/* Overall summary if available */}
                {result.results?.overallSummary && (
                  <div style={{ marginTop: 16, padding: 12, background: '#e6f7ff', borderRadius: 6 }}>
                    <Text strong style={{ color: '#1890ff' }}>Summary: </Text>
                    <Text>{result.results.overallSummary}</Text>
                  </div>
                )}

                {/* Provider and order info */}
                <Divider style={{ margin: '16px 0' }} />
                <Row gutter={16}>
                  <Col span={12}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Ordered by: {result.orderingProvider?.name || 'N/A'}
                    </Text>
                  </Col>
                  <Col span={12} style={{ textAlign: 'right' }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Order Date: {moment(result.orderDate).format('MM/DD/YYYY')}
                    </Text>
                  </Col>
                </Row>
              </Card>
            </List.Item>
          );
        }}
      />

      {/* Detail Modal */}
      <Modal
        title={
          <span>
            <ExperimentOutlined style={{ marginRight: 8 }} />
            {selectedResult?.orderDetails?.testName} - Detailed Results
          </span>
        }
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            Close
          </Button>,
          <Button key="export" icon={<DownloadOutlined />}>
            Export Results
          </Button>
        ]}
        width={800}
      >
        {selectedResult && (
          <div>
            <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Test Date">
                {moment(selectedResult.results?.resultDate).format('MMMM DD, YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="Ordering Provider">
                {selectedResult.orderingProvider?.name || 'N/A'}
              </Descriptions.Item>
              <Descriptions.Item label="Order Date">
                {moment(selectedResult.orderDate).format('MMMM DD, YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color="green">Completed</Tag>
              </Descriptions.Item>
            </Descriptions>

            {selectedResult.results?.overallSummary && (
              <div style={{ marginBottom: 16 }}>
                <Title level={5}>Overall Summary</Title>
                <Paragraph>{selectedResult.results.overallSummary}</Paragraph>
              </div>
            )}

            <Title level={5}>Detailed Results</Title>
            {selectedResult.results?.labComponents?.map(component => (
              <Card key={component.testName} size="small" style={{ marginBottom: 8 }}>
                <Row align="middle">
                  <Col span={8}>
                    <Text strong>{component.testName}</Text>
                    {component.category && (
                      <div><Tag size="small">{component.category}</Tag></div>
                    )}
                  </Col>
                  <Col span={6}>
                    <Text style={getLabValueStyle(getLabValueStatusWithFlag(component.value, component.referenceRange, component.flag))}>
                      {component.value} {component.units}
                    </Text>
                  </Col>
                  <Col span={6}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Normal: {component.referenceRange || 'N/A'}
                    </Text>
                  </Col>
                  <Col span={4} style={{ textAlign: 'right' }}>
                    {component.flag && (
                      <Tag color={getLabStatus(component) === 'critical' ? 'red' : 
                                 getLabStatus(component) === 'abnormal' ? 'orange' : 'green'}>
                        {component.flag}
                      </Tag>
                    )}
                  </Col>
                </Row>
              </Card>
            ))}
          </div>
        )}
      </Modal>

      {/* Trend Modal */}
      <Modal
        title={
          <span>
            <RiseOutlined style={{ marginRight: 8 }} />
            {selectedComponent?.testName} - Trend Analysis
          </span>
        }
        open={trendModalVisible}
        onCancel={() => setTrendModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setTrendModalVisible(false)}>
            Close
          </Button>
        ]}
        width={600}
      >
        {selectedComponent && (
          <div>
            <Paragraph>
              Trend analysis for <Text strong>{selectedComponent.testName}</Text> over time.
            </Paragraph>
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Text type="secondary">
                Trend chart functionality will be implemented with historical data
              </Text>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PatientPortalLabResults;
